/**
 * Upload Utilities
 * Shared upload logic for photo upload hooks (usePhotoUpload, useProductReadyUpload)
 */

import { FileUploadObj, FileUploadProgress } from "@/types";

/**
 * Generate a unique file ID
 * @returns Unique identifier string
 */
export function generateFileId(): string {
  return Math.random().toString(36).substring(2, 9);
}

/**
 * Create a standardized file upload object from a File
 * @param file - File to create object from
 * @returns FileUploadObj with generated ID and preview URL
 */
export function createFileUploadObj(file: File): FileUploadObj {
  return {
    file,
    id: generateFileId(),
    preview: URL.createObjectURL(file),
    uploading: false,
    uploaded: false,
  };
}

/**
 * Validate and filter files before upload
 * @param files - Files to validate
 * @param validateFn - Function to validate each file
 * @param onError - Callback to handle error messages
 * @returns Array of valid files
 */
export function validateAndFilterFiles(
  files: File[],
  validateFn: (file: File) => boolean,
  onError: (msg: string) => void
): File[] {
  return files.filter((file) => {
    try {
      validateFn(file);
      return true;
    } catch (error) {
      onError((error as Error).message);
      return false;
    }
  });
}

/**
 * Format success message with pluralization
 * @param count - Number of successful uploads
 * @param entityName - Name of entity (e.g., "photo", "file")
 * @returns Formatted success message
 */
export function formatSuccessMessage(count: number, entityName: string): string {
  const plural = count > 1 ? "s" : "";
  return `Successfully uploaded ${count} ${entityName}${plural}`;
}

/**
 * Format error message with pluralization
 * @param count - Number of failed uploads
 * @returns Formatted error message
 */
export function formatErrorMessage(count: number): string {
  const plural = count > 1 ? "s" : "";
  return `${count} upload${plural} failed. Please try again.`;
}

/**
 * Options for concurrent upload execution
 */
export interface ConcurrentUploadOptions<T> {
  items: T[];
  maxConcurrent?: number;
  uploadFn: (item: T) => Promise<boolean>;
  onProgress?: (current: number, total: number) => void;
}

/**
 * Result from concurrent upload execution
 */
export interface ConcurrentUploadResult {
  successCount: number;
  errorCount: number;
}

/**
 * Execute uploads with concurrency control
 * Manages a pool of concurrent operations, processing items in sequence
 * while maintaining a maximum number of concurrent operations
 *
 * @param options - Configuration for concurrent uploads
 * @returns Promise resolving to result with success/error counts
 *
 * @example
 * const result = await uploadWithConcurrency({
 *   items: files,
 *   maxConcurrent: 3,
 *   uploadFn: async (file) => {
 *     try {
 *       await uploadFile(file);
 *       return true;
 *     } catch (error) {
 *       console.error(error);
 *       return false;
 *     }
 *   },
 *   onProgress: (current, total) => {
 *     console.log(`${current} of ${total} completed`);
 *   },
 * });
 */
export async function uploadWithConcurrency<T>({
  items,
  maxConcurrent = 3,
  uploadFn,
  onProgress,
}: ConcurrentUploadOptions<T>): Promise<ConcurrentUploadResult> {
  let successCount = 0;
  let errorCount = 0;
  let activeUploads = 0;
  let itemIndex = 0;

  return new Promise<ConcurrentUploadResult>((resolve) => {
    const tryStartNext = () => {
      while (activeUploads < maxConcurrent && itemIndex < items.length) {
        const currentIndex = itemIndex++;
        const item = items[currentIndex];
        activeUploads++;

        uploadFn(item)
          .then((success) => {
            if (success) {
              successCount++;
            } else {
              errorCount++;
            }
          })
          .catch(() => {
            errorCount++;
          })
          .finally(() => {
            activeUploads--;
            onProgress?.(successCount + errorCount, items.length);
            tryStartNext();

            // Resolve when all items processed and no active uploads
            if (itemIndex >= items.length && activeUploads === 0) {
              resolve({ successCount, errorCount });
            }
          });
      }
    };

    // Start initial batch of concurrent uploads
    tryStartNext();
  });
}

/**
 * Progress state manager helper
 * Provides utilities for managing file and overall upload progress
 */
export interface ProgressManager {
  setFileProgress: (fileId: string, progress: FileUploadProgress) => void;
  updateFileProgress: (fileId: string, progressValue: number) => void;
  completeFile: (fileId: string) => void;
  failFile: (fileId: string) => void;
  setOverallProgress: (current: number, total: number) => void;
  resetProgress: () => void;
}

/**
 * Create a progress manager for tracking upload state
 * @param setProgress - Setter for progress state
 * @param setOverallProgress - Setter for overall progress state
 * @returns ProgressManager instance
 */
export function createProgressManager(
  setProgress: (updater: (prev: Record<string, FileUploadProgress>) => Record<string, FileUploadProgress>) => void,
  setOverallProgress: (progress: { current: number; total: number }) => void
): ProgressManager {
  return {
    setFileProgress: (fileId: string, progress: FileUploadProgress) => {
      setProgress((prev) => ({
        ...prev,
        [fileId]: progress,
      }));
    },
    updateFileProgress: (fileId: string, progressValue: number) => {
      setProgress((prev) => ({
        ...prev,
        [fileId]: { uploading: true, progress: progressValue },
      }));
    },
    completeFile: (fileId: string) => {
      setProgress((prev) => ({
        ...prev,
        [fileId]: { uploading: false, progress: 100, completed: true },
      }));
    },
    failFile: (fileId: string) => {
      setProgress((prev) => ({
        ...prev,
        [fileId]: { uploading: false, progress: 0, error: true },
      }));
    },
    setOverallProgress: (current: number, total: number) => {
      setOverallProgress({ current, total });
    },
    resetProgress: () => {
      setProgress(() => ({}));
      setOverallProgress({ current: 0, total: 0 });
    },
  };
}
